import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, ClipboardList, Search, ChevronLeft, ChevronRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import ProjectSelect from "@/components/ProjectSelect";
import FirmSelect from "@/components/FirmSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtTL, fmtNum, fmtDate } from "@/lib/utils";

const EMPTY = { project_id: "", poz_no: "", name: "", unit: "m2", quantity: 0, unit_price: 0, category: "" };
const UNITS = ["m", "m2", "m3", "adet", "kg", "ton", "lt", "takım"];
const PAGE = 50;

export default function WorkItems() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState("");
  const [firms, setFirms] = useState([]);
  const [filterFirm, setFilterFirm] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sel, setSel] = useState([]);

  const load = () => {
    const params = { skip: page * PAGE, limit: PAGE };
    if (filterProject) params.project_id = filterProject;
    if (filterFirm) params.firm_id = filterFirm;
    if (search.trim()) params.q = search.trim();
    api.get("/work-items", { params }).then(r => { setRows(r.data.items); setTotal(r.data.total); });
  };
  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/firms").then(r => setFirms(r.data));
  }, []);
  useEffect(() => { setPage(0); }, [filterProject, filterFirm, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [filterProject, filterFirm, search, page]);

  const save = async () => {
    if (!form.project_id || !form.poz_no || !form.name) { toast.error("Proje, poz no ve isim zorunlu"); return; }
    try {
      await api.post("/work-items", { ...form, quantity: Number(form.quantity), unit_price: Number(form.unit_price) });
      toast.success("İş kalemi eklendi");
      setOpen(false); setForm(EMPTY); load();
    } catch { toast.error("Kayıt başarısız"); }
  };
  const toggleSel = (r, v) => setSel(v ? [...sel, r] : sel.filter(x => x.id !== r.id));
  const remove = async (id) => { await api.delete(`/work-items/${id}`); toast.success("Silindi"); load(); };

  return (
    <div data-testid="workitems-page">
      <PageHeader title="İş Kalemleri (Pozlar)" subtitle="Firma ve projelere göre pozlar, birim fiyatlar ve üretim miktarları.">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9 w-56" placeholder="Poz veya tanım ara..." value={search} onChange={e => setSearch(e.target.value)} data-testid="workitem-search" />
        </div>
        <FirmSelect firms={firms} value={filterFirm} onChange={setFilterFirm} testid="filter-firm" />
        <ProjectSelect projects={projects} value={filterProject} onChange={setFilterProject} testid="filter-project" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-workitem-button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"><Plus className="w-4 h-4 mr-1" /> Yeni İş Kalemi</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Yeni İş Kalemi</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Proje *</Label>
                <ProjectSelect projects={projects} value={form.project_id} onChange={v => setForm({...form, project_id: v})} includeAll={false} testid="form-project" />
              </div>
              <div><Label>Poz No *</Label><Input data-testid="input-poz" value={form.poz_no} onChange={e => setForm({...form, poz_no: e.target.value})} /></div>
              <div><Label>Kategori</Label><Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="Örn: Kaba İnşaat" /></div>
              <div className="col-span-2"><Label>Açıklama / İş Adı *</Label><Input data-testid="input-wi-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div>
                <Label>Birim</Label>
                <Select value={form.unit} onValueChange={v => setForm({...form, unit: v})}>
                  <SelectTrigger data-testid="input-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Miktar</Label><Input type="number" step="0.01" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} /></div>
              <div className="col-span-2"><Label>Birim Fiyat (TL)</Label><Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({...form, unit_price: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950" onClick={save} data-testid="save-workitem-button">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <ClipboardList className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Henüz iş kalemi yok</div>
        </div>
      ) : (
        <>
        <MergeBar sel={sel} onClear={() => setSel([])} onDone={() => { setSel([]); load(); }} />
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="w-8" title="Birleştirmek için seçin" />
                <TableHead>Poz No</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead>Firma / Proje</TableHead>
                <TableHead className="text-right">Miktar</TableHead>
                <TableHead>Birim</TableHead>
                <TableHead className="text-right">Birim Fiyat</TableHead>
                <TableHead className="text-right">Toplam</TableHead>
                <TableHead>Son Tarih</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`wi-row-${r.id}`} className={sel.some(x => x.id === r.id) ? "bg-amber-500/5" : ""}>
                  <TableCell><Checkbox checked={sel.some(x => x.id === r.id)} onCheckedChange={v => toggleSel(r, v)} data-testid={`wi-select-${r.id}`} /></TableCell>
                  <TableCell className="font-mono-nums font-semibold text-xs">{r.poz_no}</TableCell>
                  <TableCell className="text-sm">{r.name}
                    {r.group_name && <div className="mt-0.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-700 dark:text-blue-400" title="Kalem eşlemesi">Eşlendi: {r.group_name}</span></div>}
                  </TableCell>
                  <TableCell className="text-xs"><div className="font-medium">{r.firm_name || r.category || "-"}</div><div className="text-muted-foreground">{r.project_name || ""}</div></TableCell>
                  <TableCell className="text-right font-mono-nums">{fmtNum(r.quantity)}</TableCell>
                  <TableCell>{r.unit}</TableCell>
                  <TableCell className="text-right font-mono-nums">{fmtTL(r.unit_price)}</TableCell>
                  <TableCell className="text-right font-mono-nums font-semibold">{fmtTL(r.total_amount ?? r.quantity * r.unit_price)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(r.last_date || r.created_at)}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-border text-sm">
            <span className="text-muted-foreground" data-testid="workitem-total">{total} kalem — sayfa {page + 1} / {Math.max(1, Math.ceil(total / PAGE))}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="workitem-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" disabled={(page + 1) * PAGE >= total} onClick={() => setPage(page + 1)} data-testid="workitem-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

function MergeBar({ sel, onClear, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!sel.length) return null;
  const units = [...new Set(sel.map(s => s.unit))];
  const names = [...new Set(sel.map(s => s.name))];
  const ok = units.length === 1 && names.length >= 2;

  const merge = async () => {
    setBusy(true);
    try {
      await api.post("/item-groups", { name: name || names[0], unit: units[0], members: names });
      toast.success(`${names.length} farklı yazım "${name || names[0]}" altında birleştirildi`);
      setName(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Birleştirilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 mb-4" data-testid="wi-merge-bar">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold">{sel.length} kalem seçildi · {names.length} farklı yazım</span>
        <Input className="flex-1 min-w-[240px]" placeholder={names[0]} value={name} onChange={e => setName(e.target.value)} data-testid="wi-merge-name" />
        <Button disabled={!ok || busy} onClick={merge} className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="wi-merge-button">Kalemleri Birleştir</Button>
        <Button variant="ghost" onClick={onClear} data-testid="wi-merge-clear">Vazgeç</Button>
      </div>
      {units.length > 1 && <p className="text-xs text-rose-600 mt-2">Farklı birimdeki kalemler birleştirilemez ({units.join(", ")}).</p>}
      {units.length === 1 && names.length < 2 && <p className="text-xs text-muted-foreground mt-2">Birleştirmek için farklı yazılmış en az iki kalem seçin.</p>}
      <p className="text-xs text-muted-foreground mt-2">Birleştirilen yazımlar fiyat uyarısı, fiyat trendi ve firma karnesinde tek kalem olarak değerlendirilir. Kalem Eşleme sayfasından geri alınabilir.</p>
    </div>
  );
}
